import { ErrorSurface, type ErrorSurfaceAction } from '@/components/ErrorSurface'
import { classifyRouteError } from '@/error/routeErrorKind'
import { getAuth } from '@/auth/authRef'
import { getRouter } from '@/router/router'
import { getString } from '@/notFound/i18n/bundle.generated'
import { openExternal } from '@/components/openExternal'
import { claimErrorSurface } from '@/errorHandler'
import { currentTerritory } from '@/marketplace'
import { musicPlanSettingsUrl } from '@/territory'
import { onConnect } from '@amazon/vinyl-tsx'
import { onReconnect } from '@/util/offlineUtil'
import { retryGuard } from '@/util/retryGuard'

/**
 * Recovery surface for a failed route transition / render or a page-loader
 * failure that would otherwise leave a blank stage. Mirrors green hornet's
 * per-ErrorSource split: a signed-out unauthorized error offers Sign-in +
 * Show-plans, a network error offers Try again, a server error offers Try
 * again + home; anything unclassified, including a failed page download
 * (whose Try again reloads), is the generic "something went wrong". A network
 * error's page also tries again by itself when the network comes back.
 */
export function ErrorPage(error?: unknown) {
    switch (classifyRouteError(error)) {
        case 'unauthorized':
            return unauthorizedSurface()
        case 'network':
            return networkSurface(retryAction())
        case 'chunkLoad':
            return defaultSurface(reloadAppAction())
        case 'server':
            return serverSurface()
        default:
            return defaultSurface(retryAction())
    }
}

function unauthorizedSurface() {
    const signedIn = getAuth().isSignedIn()
    const actions: ErrorSurfaceAction[] = signedIn
        ? [homeAction()]
        : [
              {
                  label: getString('dmusic_unauthorized_login_link'),
                  onClick: () => getAuth().signIn(),
              },
              {
                  label: getString('dmusic_unauthorized_upgrade_link'),
                  onClick: () => openExternal(musicPlanSettingsUrl(currentTerritory())),
              },
          ]
    return ErrorSurface({
        message: getString('dmusic_unauthorized_primary_text'),
        detail: getString(
            signedIn
                ? 'dmusic_unauthorized_logged_in_secondary_text'
                : 'dmusic_unauthorized_secondary_text'
        ),
        actions,
    })
}

function networkSurface(retry: RetryAction) {
    const surface = ErrorSurface({
        message: getString('dmusic_network_error_primary_text'),
        detail: getString('dmusic_network_error_secondary_text'),
        actions: [retry],
    })
    onConnect(surface, () =>
        onReconnect(() => {
            if (surface.isConnected) retry.onClick()
        })
    )
    return surface
}

function serverSurface() {
    return ErrorSurface({
        message: getString('dmusic_default_error_primary_text'),
        detail: getString('dmusic_server_error_suggestion_text'),
        actions: [retryAction(), homeAction()],
    })
}

function defaultSurface(retry: ErrorSurfaceAction) {
    return ErrorSurface({
        message: getString('dmusic_default_error_primary_text'),
        detail: getString('dmusic_default_error_secondary_text'),
        actions: [retry, homeAction()],
    })
}

/** The default error message and a reload button, for a page shown before the router can retry anything. */
export function reloadErrorPage() {
    return ErrorSurface({
        message: getString('dmusic_default_error_primary_text'),
        actions: [reloadAppAction()],
        centered: true,
    })
}

type RetryAction = Extract<ErrorSurfaceAction, { readonly onClick: () => void }>

function retryAction(): RetryAction {
    const guard = retryGuard(() => getRouter().hardReload())
    return {
        label: getString('dmusic_network_error_try_again_link'),
        onClick: guard.press,
        pending$: guard.pending$,
    }
}

// The browser keeps a failed module fetch for the life of the document, so only a reload refetches it.
function reloadAppAction(): ErrorSurfaceAction {
    return {
        label: getString('dmusic_network_error_try_again_link'),
        onClick: () => location.reload(),
    }
}

/** Swaps a page shell whose load failed for the error surface, reporting the error without a second toast. */
export function replaceWithErrorPage<E>(view: Element, error: E, report: (error: E) => void): void {
    claimErrorSurface(error)
    report(error)
    view.replaceChildren(ErrorPage(error))
}

function homeAction(): ErrorSurfaceAction {
    return {
        href: '/',
        label: getString('dmusic_default_error_homepage_link'),
        navigateOptions: { scroll: 'restore' },
    }
}
